import { Link } from 'react-router-dom';
import { useRef, useState } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function FeaturedGuides() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const { ref: sectionRef, isVisible } = useScrollAnimation<HTMLElement>({ threshold: 0.2 });

  const guides = [
    {
      title: 'Best Tech Gifts Under $100',
      count: '25 products',
      slug: '/gift-guides/tech-under-100',
      image: 'https://public.readdy.ai/ai/img_res/47ccaec3eb93f6295bc040059d02cfd4.jpg'
    },
    {
      title: 'Luxury Gifts That Impress',
      count: '18 products',
      slug: '/gift-guides/luxury-gifts',
      image: 'https://readdy.ai/api/search-image?query=Elegant%20luxury%20gift%20items%20including%20designer%20accessories%20premium%20watch%20leather%20goods%20and%20high-end%20products%20displayed%20on%20marble%20surface%20with%20gold%20accents%20and%20festive%20Christmas%20elements%20sophisticated%20professional%20photography&width=420&height=300&seq=guide-luxury-001&orientation=landscape'
    },
    {
      title: 'Perfect Gifts for Mom',
      count: '32 products',
      slug: '/gift-guides',
      image: 'https://readdy.ai/api/search-image?query=Thoughtful%20gifts%20for%20mothers%20including%20spa%20products%20jewelry%20cozy%20items%20flowers%20and%20elegant%20accessories%20arranged%20beautifully%20with%20soft%20lighting%20Christmas%20decorations%20and%20warm%20festive%20atmosphere%20professional%20lifestyle%20photography&width=420&height=300&seq=guide-mom-001&orientation=landscape'
    },
    {
      title: 'Best Gifts for Dad',
      count: '28 products',
      slug: '/gift-guides',
      image: 'https://readdy.ai/api/search-image?query=Masculine%20gifts%20for%20fathers%20including%20tools%20gadgets%20grooming%20products%20leather%20accessories%20and%20hobby%20items%20displayed%20on%20wooden%20surface%20with%20subtle%20Christmas%20decorations%20professional%20product%20photography%20style&width=420&height=300&seq=guide-dad-001&orientation=landscape'
    },
    {
      title: 'Unique Stocking Stuffers',
      count: '45 products',
      slug: '/gift-guides',
      image: 'https://readdy.ai/api/search-image?query=Small%20creative%20stocking%20stuffer%20gifts%20including%20mini%20gadgets%20candies%20accessories%20and%20fun%20items%20arranged%20with%20Christmas%20stockings%20pine%20branches%20and%20festive%20decorations%20bright%20cheerful%20holiday%20photography%20style&width=420&height=300&seq=guide-stocking-001&orientation=landscape'
    },
    {
      title: 'Cozy Home Gifts',
      count: '36 products',
      slug: '/gift-guides',
      image: 'https://readdy.ai/api/search-image?query=Cozy%20home%20gifts%20including%20soft%20blankets%20candles%20decorative%20pillows%20warm%20lighting%20and%20comfort%20items%20arranged%20in%20inviting%20living%20room%20setting%20with%20Christmas%20decorations%20and%20warm%20festive%20ambiance%20lifestyle%20photography&width=420&height=300&seq=guide-home-001&orientation=landscape'
    },
    {
      title: 'Gifts for Teens',
      count: '41 products',
      slug: '/gift-guides',
      image: 'https://readdy.ai/api/search-image?query=Trendy%20gifts%20for%20teenagers%20including%20fashion%20accessories%20tech%20gadgets%20gaming%20items%20and%20popular%20products%20displayed%20on%20modern%20colorful%20background%20with%20festive%20Christmas%20elements%20contemporary%20youth-focused%20photography&width=420&height=300&seq=guide-teens-001&orientation=landscape'
    },
    {
      title: 'Eco-Friendly Gifts',
      count: '22 products',
      slug: '/gift-guides',
      image: 'https://readdy.ai/api/search-image?query=Sustainable%20eco-friendly%20gifts%20including%20reusable%20products%20natural%20materials%20organic%20items%20and%20green%20alternatives%20arranged%20on%20natural%20wood%20surface%20with%20plants%20and%20minimal%20Christmas%20decorations%20clean%20environmental%20photography&width=420&height=300&seq=guide-eco-001&orientation=landscape'
    }
  ];

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const scrollAmount = 444;
      const newIndex = direction === 'left' 
        ? Math.max(0, activeIndex - 1)
        : Math.min(guides.length - 1, activeIndex + 1);
      setActiveIndex(newIndex);
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section 
      ref={sectionRef}
      className="py-20 bg-gradient-to-b from-[#1B4332] to-[#2D5F4C] relative overflow-hidden"
    >
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className={`absolute top-10 left-10 text-white text-9xl transition-all duration-1000 ${isVisible ? 'opacity-5 translate-y-0' : 'opacity-0 -translate-y-10'}`}>❄</div>
        <div className={`absolute top-40 right-20 text-white text-7xl transition-all duration-1000 delay-200 ${isVisible ? 'opacity-5 translate-x-0' : 'opacity-0 translate-x-10'}`}>✨</div>
        <div className={`absolute bottom-20 left-1/4 text-white text-8xl animate-float transition-all duration-1000 delay-300 ${isVisible ? 'opacity-5' : 'opacity-0'}`}>🎄</div>
        <div className={`absolute bottom-40 right-1/3 text-white text-6xl animate-twinkle transition-all duration-1000 delay-400 ${isVisible ? 'opacity-5' : 'opacity-0'}`}>⭐</div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div 
          className={`flex items-center justify-between mb-12 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div>
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-2">
              Popular Gift Guides
            </h2>
            <p className="text-xl text-gray-300">
              Expertly curated collections for every occasion
            </p>
          </div>
          <Link 
            to="/gift-guides"
            className="hidden lg:flex items-center gap-2 text-[#D4AF37] font-semibold hover:gap-4 transition-all whitespace-nowrap group"
          >
            View All Guides
            <i className="ri-arrow-right-line group-hover:translate-x-2 transition-transform"></i>
          </Link>
        </div>

        <div className="relative">
          <button
            onClick={() => scroll('left')}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-12 h-12 bg-[#D4AF37] rounded-full flex items-center justify-center text-white hover:scale-110 hover:rotate-12 transition-all duration-300 shadow-xl hover:shadow-[#D4AF37]/50 active:scale-95"
          >
            <i className="ri-arrow-left-line text-xl"></i>
          </button>

          <div 
            ref={scrollRef}
            className="flex gap-6 overflow-x-auto scrollbar-hide scroll-smooth snap-x snap-mandatory"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {guides.map((guide, index) => (
              <Link
                key={index}
                to={guide.slug}
                className={`flex-shrink-0 w-[420px] group cursor-pointer snap-start transition-all duration-700 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="relative rounded-2xl overflow-hidden shadow-2xl group-hover:shadow-[#D4AF37]/20 transition-shadow duration-500">
                  <div className="w-full h-[520px] overflow-hidden">
                    <img 
                      src={guide.image} 
                      alt={guide.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent group-hover:from-black/90 transition-all duration-500"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-8 transform group-hover:translate-y-[-8px] transition-transform duration-500">
                    <div className="inline-block px-4 py-1 bg-white/20 backdrop-blur-sm rounded-full mb-4 group-hover:bg-[#D4AF37]/80 transition-colors duration-300">
                      <span className="text-white text-sm font-medium">{guide.count}</span>
                    </div>
                    <h3 className="text-3xl font-bold text-white mb-2 group-hover:text-[#D4AF37] transition-colors duration-300">
                      {guide.title}
                    </h3>
                    <div className="flex items-center gap-2 text-white/80 group-hover:text-white transition-colors">
                      <span className="text-sm">Explore Guide</span>
                      <i className="ri-arrow-right-line group-hover:translate-x-2 transition-transform duration-300"></i>
                    </div>
                  </div>
                  {/* Shimmer effect on hover */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <button
            onClick={() => scroll('right')}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-12 h-12 bg-[#D4AF37] rounded-full flex items-center justify-center text-white hover:scale-110 hover:-rotate-12 transition-all duration-300 shadow-xl hover:shadow-[#D4AF37]/50 active:scale-95"
          >
            <i className="ri-arrow-right-line text-xl"></i>
          </button>
        </div>

        {/* Progress Dots */}
        <div className="flex justify-center gap-2 mt-8">
          {guides.map((_, index) => (
            <button
              key={index}
              onClick={() => {
                setActiveIndex(index);
                scrollRef.current?.scrollTo({
                  left: index * 444,
                  behavior: 'smooth'
                });
              }}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                index === activeIndex 
                  ? 'w-8 bg-[#D4AF37]' 
                  : 'bg-white/30 hover:bg-white/50'
              }`}
            />
          ))}
        </div>

        <Link 
          to="/gift-guides"
          className="lg:hidden flex items-center justify-center gap-2 text-[#D4AF37] font-semibold mt-8 hover:gap-4 transition-all"
        >
          View All Guides
          <i className="ri-arrow-right-line"></i>
        </Link>
      </div>
    </section>
  );
}
